// Shared CNI options for the cluster creation forms (OpenStack and BYO).
// Both the CNI and the Cilium kube-proxy mode are immutable once the cluster is created.
// To add a CNI setting: add its options and default here, map it in toCniConfig, and render it
// in ClusterNetworkingFields. The forms and the cluster body builder need no changes.
import type { ClusterCni, CniConfig } from 'k8s/components/kaapi/clusters/action-helpers'

// FR-001: CNI choices; Calico is the default
export const DEFAULT_CNI: ClusterCni = 'calico'

// FR-001: info says how kaapi deploys each CNI (shown in the card's info tooltip)
export const CNI_OPTIONS: Array<{ value: ClusterCni; label: string; info: string }> = [
  {
    value: 'calico',
    label: 'Calico',
    info: 'Pod traffic between nodes uses IP-in-IP tunnels; kube-proxy routes Service traffic.',
  },
  {
    value: 'cilium',
    label: 'Cilium',
    info:
      'Pod traffic between nodes uses VXLAN tunnels; Cilium can also route Service traffic ' +
      'with eBPF, replacing kube-proxy.',
  },
]

// FR-002: kube-proxy mode choices for Cilium; replacement (no kube-proxy) is the default
export enum KubeProxyMode {
  Replacement = 'replacement',
  KubeProxy = 'kube-proxy',
}

export const KUBE_PROXY_MODE_OPTIONS = [
  {
    value: KubeProxyMode.Replacement,
    label: 'Cilium eBPF (recommended)',
    description: 'Cilium uses eBPF instead of kube-proxy, for faster Service routing.',
  },
  {
    value: KubeProxyMode.KubeProxy,
    label: 'Standard kube-proxy',
    description: 'Uses kube-proxy for Service routing; Cilium handles pod networking.',
  },
]

// FR-003: Maps the form's kube-proxy mode to the `cni.kubeProxyReplacement` variable
export const isKubeProxyReplacement = (mode?: KubeProxyMode) => mode !== KubeProxyMode.KubeProxy

export interface CniFormParams {
  cni?: ClusterCni
  kubeProxyMode?: KubeProxyMode
}

// FR-006: Networking step description, shared by the OpenStack and BYO create forms
export const NETWORKING_STEP_DESCRIPTION =
  'Choose the cluster network plugin (CNI) and related network settings.'

// FR-004: Initial form values for every CNI setting, spread into the forms' initialValues
export const CNI_INITIAL_VALUES: Required<CniFormParams> = {
  cni: DEFAULT_CNI,
  kubeProxyMode: KubeProxyMode.Replacement,
}

// FR-005: Builds the cluster body's cniConfig from the form values; Cilium-only settings are
// included only when Cilium is selected
export const toCniConfig = ({ cni = DEFAULT_CNI, kubeProxyMode }: CniFormParams): CniConfig =>
  cni === 'cilium'
    ? { type: cni, kubeProxyReplacement: isKubeProxyReplacement(kubeProxyMode) }
    : { type: cni }
